import { gql } from '@/lib/api';
import { toCsv } from '@/lib/csv';

/**
 * OS-1726 — visor de auditoría de plataforma. El alcance por rol, los nombres de las acciones y el
 * ocultamiento de datos sensibles los decide el API; la consola solo presenta.
 */
export type AuditScope = 'all' | 'tenant' | 'own';

export interface AuditLogEntry {
  id: string;
  createdAt: string;
  action: string;
  actionLabel: string;
  category: string;
  outcome: 'success' | 'failure';
  actorId: string | null;
  actorEmail: string | null;
  actorRole: string | null;
  targetType: string | null;
  targetId: string | null;
  targetLabel: string | null;
  tenantId: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  /** JSON con los valores sensibles ya ocultos por el API. */
  detail: string | null;
}

export interface AuditLogPage {
  entries: AuditLogEntry[];
  nextCursor: string | null;
  scope: AuditScope;
}

export interface AuditFilterOptions {
  scope: AuditScope;
  actions: { action: string; label: string; category: string; outcome: string }[];
  categories: string[];
  targetTypes: string[];
  admins: { id: string; email: string; platformRole: string }[];
}

export interface AuditLogFilter {
  tenantId?: string;
  adminId?: string;
  actions?: string[];
  category?: string;
  targetType?: string;
  result?: 'success' | 'failure';
  /** ISO, inclusive. */
  from?: string;
  /** ISO, exclusive. */
  to?: string;
}

export const AUDIT_PAGE_SIZE = 50;
/** Tope de filas de una exportación (el API pagina de a 200). */
export const AUDIT_EXPORT_MAX_ROWS = 10_000;
const EXPORT_PAGE_SIZE = 200;

export const auditLogsQueryKey = (filter: AuditLogFilter): readonly unknown[] => [
  'platform-audit-logs',
  filter,
];
export const AUDIT_OPTIONS_QUERY_KEY = ['platform-audit-filter-options'] as const;

const ENTRY_FIELDS = `id createdAt action actionLabel category outcome actorId actorEmail actorRole
  targetType targetId targetLabel tenantId ipAddress userAgent detail`;

export async function getAuditLogs(
  filter: AuditLogFilter,
  page: { take?: number; cursor?: string | null } = {},
): Promise<AuditLogPage> {
  const data = await gql<{ platformAuditLogs: AuditLogPage }>(
    `query AuditLogs($filter: PlatformAuditLogFilterInput) {
      platformAuditLogs(filter: $filter) { scope nextCursor entries { ${ENTRY_FIELDS} } }
    }`,
    {
      filter: {
        ...filter,
        take: page.take ?? AUDIT_PAGE_SIZE,
        ...(page.cursor && { cursor: page.cursor }),
      },
    },
  );
  return data.platformAuditLogs;
}

export async function getAuditFilterOptions(): Promise<AuditFilterOptions> {
  const data = await gql<{ platformAuditFilterOptions: AuditFilterOptions }>(
    `query AuditFilterOptions { platformAuditFilterOptions {
      scope categories targetTypes
      actions { action label category outcome }
      admins { id email platformRole }
    } }`,
  );
  return data.platformAuditFilterOptions;
}

/**
 * Recorre todas las páginas del mismo filtro que la tabla (misma consulta, mismo orden y mismo
 * ocultamiento), así el CSV coincide con lo que se ve. Se detiene en el tope.
 */
export async function collectAuditLogs(
  filter: AuditLogFilter,
  maxRows = AUDIT_EXPORT_MAX_ROWS,
): Promise<{ entries: AuditLogEntry[]; truncated: boolean }> {
  const entries: AuditLogEntry[] = [];
  let cursor: string | null = null;
  do {
    const page: AuditLogPage = await getAuditLogs(filter, { take: EXPORT_PAGE_SIZE, cursor });
    entries.push(...page.entries);
    cursor = page.nextCursor;
  } while (cursor && entries.length < maxRows);
  return { entries: entries.slice(0, maxRows), truncated: Boolean(cursor) || entries.length > maxRows };
}

const CSV_HEADER = [
  'fecha',
  'accion',
  'codigo',
  'resultado',
  'actor',
  'rol',
  'tipo_entidad',
  'entidad',
  'entidad_id',
  'ip',
  'navegador',
  'detalle',
];

/** CSV con las mismas columnas de la tabla (más el código de la acción). */
export function auditLogsToCsv(entries: AuditLogEntry[]): string {
  return toCsv(
    CSV_HEADER,
    entries.map((entry) => [
      entry.createdAt,
      entry.actionLabel,
      entry.action,
      entry.outcome === 'failure' ? 'fallido' : 'exitoso',
      entry.actorEmail ?? (entry.actorId ? entry.actorId : 'Sistema'),
      entry.actorRole,
      entry.targetType,
      entry.targetLabel,
      entry.targetId,
      entry.ipAddress,
      entry.userAgent,
      entry.detail,
    ]),
  );
}
